import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { toast } from "sonner";
import {
  useAdminOfferTemplates,
  useOfferTemplateLines,
  useUpsertOfferTemplate,
  useUpsertOfferTemplateLine,
  useDeleteOfferTemplateLine,
} from "@/hooks/use-product-catalog";
import {
  useOfferLineTemplates,
  type OfferLineTemplate,
  type OfferTemplate,
  type OfferTemplateLine,
} from "@/hooks/use-offers";
import { cn } from "@/lib/utils";
import { CatalogProductPicker } from "./CatalogProductPicker";

export function OfferTemplatesAccordion() {
  const { data: templates = [], isLoading } = useAdminOfferTemplates();
  const upsertTemplate = useUpsertOfferTemplate();
  const [newTplOpen, setNewTplOpen] = useState(false);
  const [newTplDraft, setNewTplDraft] = useState<Partial<OfferTemplate>>({
    kind: "size",
    is_active: true,
  });

  async function createTemplate() {
    if (!newTplDraft.code?.trim() || !newTplDraft.name?.trim()) {
      toast.error("Kode og navn er påkrevd");
      return;
    }
    try {
      await upsertTemplate.mutateAsync({
        code: newTplDraft.code.trim(),
        name: newTplDraft.name.trim(),
        description: newTplDraft.description ?? null,
        kind: newTplDraft.kind ?? "size",
        sort_order: newTplDraft.sort_order ?? 999,
        is_active: true,
      });
      setNewTplDraft({ kind: "size", is_active: true });
      setNewTplOpen(false);
      toast.success("Tilbudsmal opprettet");
    } catch (err) {
      toast.error(`Kunne ikke opprette: ${(err as Error).message}`);
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Tilbudsmaler forhåndsfyller nye tilbud med linjer (size = XS/S/M/L,
          addon = stills/reise).
        </p>
        <Button size="sm" onClick={() => setNewTplOpen((v) => !v)}>
          <SrIcon name="plus" className="w-3.5 h-3.5 mr-1" />
          Ny mal
        </Button>
      </div>

      {newTplOpen && (
        <div className="rounded-md border bg-muted/30 p-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <div>
              <Label className="text-xs">Kode</Label>
              <Input
                value={newTplDraft.code ?? ""}
                onChange={(e) => setNewTplDraft({ ...newTplDraft, code: e.target.value })}
                placeholder="xs / s / m / stills…"
                className="h-8 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Navn</Label>
              <Input
                value={newTplDraft.name ?? ""}
                onChange={(e) => setNewTplDraft({ ...newTplDraft, name: e.target.value })}
                className="h-8 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select
                value={newTplDraft.kind}
                onValueChange={(v) =>
                  setNewTplDraft({ ...newTplDraft, kind: v as "size" | "addon" })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="size">Størrelse (XS/S/M/L)</SelectItem>
                  <SelectItem value="addon">Tillegg (stills/reise)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Sort</Label>
              <Input
                type="number"
                value={String(newTplDraft.sort_order ?? 999)}
                onChange={(e) =>
                  setNewTplDraft({ ...newTplDraft, sort_order: Number(e.target.value) || 0 })
                }
                className="h-8 text-xs"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setNewTplOpen(false);
                setNewTplDraft({ kind: "size", is_active: true });
              }}
            >
              Avbryt
            </Button>
            <Button size="sm" onClick={createTemplate} disabled={upsertTemplate.isPending}>
              Opprett
            </Button>
          </div>
        </div>
      )}

      <Accordion type="multiple" className="border rounded-md">
        {templates.map((tpl) => (
          <TemplateAccordionItem key={tpl.id} template={tpl} />
        ))}
      </Accordion>
    </div>
  );
}

function TemplateAccordionItem({ template }: { template: OfferTemplate }) {
  const { data: lines = [], isLoading } = useOfferTemplateLines(template.id);
  const { data: catalog = [] } = useOfferLineTemplates();
  const upsertLine = useUpsertOfferTemplateLine();
  const deleteLine = useDeleteOfferTemplateLine();
  const upsertTemplate = useUpsertOfferTemplate();
  const [newLineDraft, setNewLineDraft] = useState<Partial<OfferTemplateLine> | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Partial<OfferTemplateLine>>>({});

  function patchDraft(id: string, patch: Partial<OfferTemplateLine>) {
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] ?? {}), ...patch } }));
  }

  function getValue<K extends keyof OfferTemplateLine>(
    line: OfferTemplateLine,
    key: K,
  ): OfferTemplateLine[K] {
    const draft = drafts[line.id];
    return (draft && key in draft ? draft[key] : line[key]) as OfferTemplateLine[K];
  }

  /** Slår opp katalog-rad fra id (eller fra line.line_template hvis vi har joinet data). */
  function resolveCatalogEntry(id: string | null | undefined): OfferLineTemplate | null {
    if (!id) return null;
    return catalog.find((c) => c.id === id) ?? null;
  }

  async function saveLine(line: OfferTemplateLine) {
    const draft = drafts[line.id];
    if (!draft) return;
    // Hvis admin har byttet katalog-rad, oppdater også de utledede feltene
    // for bakoverkompat (NOT NULL-kolonner på offer_template_lines).
    const nextCatalogId = draft.line_template_id ?? line.line_template_id;
    const lt = resolveCatalogEntry(nextCatalogId) ?? line.line_template;
    // Etter at redundante kolonner er droppet (VIB-701-followup) sender vi
    // kun quantity/sort_order/line_template_id — phase/description/cost_type
    // /pricing_type/unit utledes ved oppslag.
    void lt;
    const payload: Partial<OfferTemplateLine> & { id: string; template_id: string } = {
      id: line.id,
      template_id: template.id,
      ...draft,
    };
    try {
      await upsertLine.mutateAsync(payload);
      setDrafts((d) => {
        const next = { ...d };
        delete next[line.id];
        return next;
      });
      toast.success("Lagret");
    } catch (err) {
      toast.error(`Kunne ikke lagre: ${(err as Error).message}`);
    }
  }

  async function createLine() {
    if (!newLineDraft?.line_template_id) {
      toast.error("Velg et produkt fra katalogen");
      return;
    }
    const lt = resolveCatalogEntry(newLineDraft.line_template_id);
    if (!lt) {
      toast.error("Kan ikke finne katalog-raden");
      return;
    }
    try {
      await upsertLine.mutateAsync({
        template_id: template.id,
        line_template_id: lt.id,
        quantity: newLineDraft.quantity ?? 0,
        sort_order: newLineDraft.sort_order ?? 999,
      });
      setNewLineDraft(null);
      toast.success("Linje lagt til");
    } catch (err) {
      toast.error(`Kunne ikke opprette: ${(err as Error).message}`);
    }
  }

  async function removeLine(line: OfferTemplateLine) {
    const label = line.line_template?.name ?? "linja";
    if (!window.confirm(`Slett "${label}"?`)) return;
    try {
      await deleteLine.mutateAsync({ id: line.id, template_id: template.id });
      toast.success("Slettet");
    } catch (err) {
      toast.error(`Kunne ikke slette: ${(err as Error).message}`);
    }
  }

  async function toggleActive() {
    try {
      await upsertTemplate.mutateAsync({
        id: template.id,
        is_active: !template.is_active,
      });
      toast.success(template.is_active ? "Mal deaktivert" : "Mal aktivert");
    } catch (err) {
      toast.error(`Kunne ikke endre status: ${(err as Error).message}`);
    }
  }

  return (
    <AccordionItem value={template.id} className={cn(!template.is_active && "opacity-50")}>
      <AccordionTrigger className="px-3 py-2 hover:no-underline">
        <div className="flex items-center gap-3 flex-1">
          <span className="font-medium text-sm">{template.name}</span>
          <span className="text-xs text-muted-foreground font-mono">
            {template.code} · {template.kind}
          </span>
          {!template.is_active && (
            <span className="text-xs text-destructive">deaktivert</span>
          )}
          <span className="text-xs text-muted-foreground ml-auto mr-3">
            {lines.length} linjer
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-3 pb-3">
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Button size="sm" variant="outline" onClick={toggleActive}>
              {template.is_active ? "Deaktiver mal" : "Aktiver mal"}
            </Button>
            <Button
              size="sm"
              onClick={() =>
                setNewLineDraft({ quantity: 0, sort_order: 999 })
              }
              disabled={!!newLineDraft}
            >
              <SrIcon name="plus" className="w-3.5 h-3.5 mr-1" />
              Ny mal-linje
            </Button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-4">
              <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border/50 text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left py-1.5 px-2">Produkt</th>
                    <th className="text-left py-1.5 px-2 w-40">Type · Enhet</th>
                    <th className="text-right py-1.5 px-2 w-20">Mengde</th>
                    <th className="text-right py-1.5 px-2 w-16">Sort</th>
                    <th className="w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {newLineDraft && (
                    <tr className="border-b border-primary/30 bg-primary/5">
                      <td className="py-1 px-2">
                        <CatalogProductPicker
                          value={newLineDraft.line_template_id ?? null}
                          onChange={(id) =>
                            setNewLineDraft({ ...newLineDraft, line_template_id: id })
                          }
                        />
                      </td>
                      <td className="py-1 px-2 text-muted-foreground text-xs">
                        {(() => {
                          const lt = resolveCatalogEntry(newLineDraft.line_template_id);
                          if (!lt) return "—";
                          return `${lt.cost_type === "external" ? "Ekstern" : "Intern"} · ${lt.default_unit}`;
                        })()}
                      </td>
                      <td className="py-1 px-2">
                        <Input
                          type="number"
                          step="0.25"
                          value={String(newLineDraft.quantity ?? 0)}
                          onChange={(e) =>
                            setNewLineDraft({
                              ...newLineDraft,
                              quantity: Number(e.target.value) || 0,
                            })
                          }
                          className="h-7 text-xs text-right tabular-nums"
                        />
                      </td>
                      <td className="py-1 px-2">
                        <Input
                          type="number"
                          value={String(newLineDraft.sort_order ?? 999)}
                          onChange={(e) =>
                            setNewLineDraft({
                              ...newLineDraft,
                              sort_order: Number(e.target.value) || 0,
                            })
                          }
                          className="h-7 text-xs text-right tabular-nums"
                        />
                      </td>
                      <td className="py-1 px-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" className="h-7 px-2 text-xs" onClick={createLine}>
                            Lagre
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            onClick={() => setNewLineDraft(null)}
                          >
                            Avbryt
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {lines.map((line) => {
                    const dirty = !!drafts[line.id];
                    const currentTemplateId =
                      (drafts[line.id]?.line_template_id ?? line.line_template_id) || null;
                    const currentTemplate =
                      resolveCatalogEntry(currentTemplateId) ?? line.line_template ?? null;
                    const hasMatch = !!currentTemplate;
                    return (
                      <tr
                        key={line.id}
                        className={cn(
                          "border-b border-border/30 hover:bg-muted/20",
                          dirty && "bg-amber-50/40",
                          !hasMatch && "bg-destructive/5",
                        )}
                      >
                        <td className="py-1 px-2">
                          {hasMatch ? (
                            <CatalogProductPicker
                              value={currentTemplateId}
                              onChange={(id) =>
                                patchDraft(line.id, { line_template_id: id })
                              }
                            />
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <CatalogProductPicker
                                  value={null}
                                  onChange={(id) =>
                                    patchDraft(line.id, { line_template_id: id })
                                  }
                                  placeholder="⚠ Mismatch — velg produkt"
                                />
                              </div>
                              <span
                                className="text-[10px] text-destructive truncate max-w-[180px]"
                                title={line.line_template_id}
                              >
                                ⚠ Mangler katalogkobling ({line.line_template_id})
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="py-1 px-2 text-muted-foreground text-xs">
                          {currentTemplate
                            ? `${currentTemplate.cost_type === "external" ? "Ekstern" : "Intern"} · ${currentTemplate.default_unit}`
                            : "—"}
                        </td>
                        <td className="py-1 px-2">
                          <Input
                            type="number"
                            step="0.25"
                            value={String(getValue(line, "quantity") ?? 0)}
                            onChange={(e) =>
                              patchDraft(line.id, { quantity: Number(e.target.value) || 0 })
                            }
                            className="h-7 text-xs text-right tabular-nums"
                          />
                        </td>
                        <td className="py-1 px-2">
                          <Input
                            type="number"
                            value={String(getValue(line, "sort_order") ?? 0)}
                            onChange={(e) =>
                              patchDraft(line.id, {
                                sort_order: Number(e.target.value) || 0,
                              })
                            }
                            className="h-7 text-xs text-right tabular-nums"
                          />
                        </td>
                        <td className="py-1 px-2">
                          <div className="flex items-center justify-end gap-1">
                            {dirty && (
                              <Button
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => saveLine(line)}
                              >
                                Lagre
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                              onClick={() => removeLine(line)}
                            >
                              <SrIcon name="trash2" className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
